import { useState } from 'react';
import html2canvas from 'html2canvas';

/**
 * Handles saving a voice/chat session as a .log file + .png screenshot.
 *
 * @param {object} opts
 * @param {Array}    opts.messages          - message array
 * @param {object}   opts.loadedProfileData - current profile (or null)
 * @param {string}   opts.selectedBackend   - backend key
 * @param {string}   opts.selectedModel     - model string
 * @param {object}   opts.scrollRef         - React ref pointing to the scrollable history element
 * @param {Function} opts.log               - (type, msg) => void
 */
export function useSaveSession({ messages, loadedProfileData, selectedBackend, selectedModel, scrollRef, log }) {
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'

  const handleSaveSession = async () => {
    if (saveStatus === 'saving') return;
    setSaveStatus('saving');
    log('system', 'Saving session…');

    const now  = new Date();
    const date = [now.getFullYear(), String(now.getMonth() + 1).padStart(2, '0'), String(now.getDate()).padStart(2, '0')].join('-');
    const time = [String(now.getHours()).padStart(2, '0'), String(now.getMinutes()).padStart(2, '0'), String(now.getSeconds()).padStart(2, '0')].join('-');
    const profileSlug  = (loadedProfileData?.name || 'default').replace(/[^a-z0-9]/gi, '-').toLowerCase();
    const firstUserMsg = messages.find(m => m.isUser);
    const titleSlug    = firstUserMsg
      ? firstUserMsg.text.slice(0, 30).replace(/[^a-zA-Z0-9 ]/g, '').trim().replace(/\s+/g, '-').toLowerCase()
      : 'session';
    const base = `voice_${date}_${time}_${profileSlug}_${titleSlug}`;

    const triggerDownload = (href, filename) => {
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(href);
    };

    try {
      // 1. Build and download .log file
      const userMsgs = messages.filter(m => m.isUser).length;
      const botMsgs  = messages.filter(m => !m.isUser).length;
      const divider  = '─'.repeat(50);
      const logLines = [
        '╔══════════════════════════════════════════════════╗',
        '║         ABC Bank IVA — Voice Session Log         ║',
        '╚══════════════════════════════════════════════════╝',
        '',
        `  Date     : ${now.toLocaleString()}`,
        `  Profile  : ${loadedProfileData?.name || 'Default'}`,
        `  Backend  : ${selectedBackend.toUpperCase()}`,
        `  Model    : ${selectedModel || 'Unknown'}`,
        `  Messages : ${userMsgs} from you, ${botMsgs} from assistant`,
        '',
        divider,
        '',
        ...messages.flatMap(msg => {
          const role = msg.isUser ? 'YOU' : 'ASSISTANT';
          const ts   = msg.id ? new Date(msg.id).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
          return [`[${role}${ts ? '  ·  ' + ts : ''}]`, msg.text || '(empty)', ''];
        }),
        divider,
        `End of session — ${messages.length} total messages`,
      ];
      const logBlob = new Blob([logLines.join('\n')], { type: 'text/plain' });
      triggerDownload(URL.createObjectURL(logBlob), `${base}.log`);

      // 2. Screenshot the conversation panel
      const el = scrollRef?.current;
      if (el) {
        const fullHeight = el.scrollHeight;
        const fullWidth  = el.offsetWidth;
        el.scrollTop = 0;
        const canvas = await html2canvas(el, {
          scale: 2,
          useCORS: true,
          scrollX: 0, scrollY: 0,
          width: fullWidth, height: fullHeight,
          windowWidth: fullWidth, windowHeight: fullHeight,
          onclone: (_doc, clonedEl) => {
            clonedEl.style.overflow  = 'visible';
            clonedEl.style.height    = `${fullHeight}px`;
            clonedEl.style.maxHeight = 'none';
          },
        });
        canvas.toBlob(blob => {
          triggerDownload(URL.createObjectURL(blob), `${base}.png`);
        }, 'image/png');
      }

      setSaveStatus('saved');
      log('system', `Session saved: ${base}`);
    } catch (e) {
      console.error('[useSaveSession] Failed:', e);
      setSaveStatus('error');
      log('error', `Save failed: ${e.message}`);
    }

    setTimeout(() => setSaveStatus(null), 2500);
  };

  return { saveStatus, handleSaveSession };
}
